import { type ComponentPropsWithoutRef, type ReactNode, useId } from 'react';
import classNames from 'classnames';

import styles from './TooltipTrigger.module.scss';

export interface TooltipTriggerProps
    extends Omit<ComponentPropsWithoutRef<'button'>, 'type' | 'aria-label' | 'aria-describedby'> {
    children: ReactNode;
    /** The button's name, when its content is not one: an icon or a dot. */
    label?: string;
    /** What the tooltip explains, given to a screen reader as the button's description. */
    description?: ReactNode;
    /** An element elsewhere that already describes the button, such as a visually hidden list. */
    describedBy?: string;
}

/**
 * What a Tooltip opens from when the thing it explains is not a control: a plain button, so a
 * keyboard or touch user reaches the tooltip the way a pointer does. Focus opens the tooltip
 * and pressing it does nothing more. A screen reader does not read the tooltip, so the same
 * text describes the button through a hidden element, which still counts while hidden.
 */
export function TooltipTrigger(props: TooltipTriggerProps) {
    const { children, label, description, describedBy, className, ...rest } = props;
    const descriptionId = useId();
    const describedByIds =
        [description == null ? undefined : descriptionId, describedBy]
            .filter((id) => id !== undefined)
            .join(' ') || undefined;

    return (
        <>
            <button
                {...rest}
                type="button"
                className={classNames(styles.trigger, className)}
                aria-label={label}
                aria-describedby={describedByIds}>
                {children}
            </button>
            {description != null && (
                <span id={descriptionId} hidden>
                    {description}
                </span>
            )}
        </>
    );
}
